import { ActionInternal, LedRule } from "@/keyboard";
import { defineStore } from "pinia";

export const AREAS = [
  "keys",
  "leds",
  "mouse",
  "general",
  "keycaps",
  "detect",
] as const;
export const CONFIG_AREAS = ["keys", "leds", "mouse", "general"] as const; // Areas that affect keyboard config
export const USER_AREAS = ["keycaps", "detect"] as const; // Areas that have no effect on keyboard config
export type AreasTuple = typeof AREAS;
export type AreaType = AreasTuple[number];

export const AREA_TOOLTIPS: Record<AreaType, string> = {
  keys: "Key functions",
  leds: "Keyboard LED patterns",
  mouse: "Mouse emulation settings",
  general: "General keyboard settings",
  keycaps: "Keycap colors in the browser",
  detect: "Detect firmware configuration",
};

export const useHelpStore = defineStore("help", {
  state: () => {
    return {
      configDetailsExpand: false,

      currentGuideArea: "keys" as AreaType,
      selectedArea: null as null | AreaType,

      selectedKey: null as null | { row: number; col: number },
      keyMenuExpand: false,
      overrideKeyAction: null as null | ActionInternal,
      overrideTransAction: null as null | ActionInternal,

      selectedLedRule: null as null | number,
      expandLedRule: false,
      overrideLedRules: null as null | LedRule[],

      showMousePlot: false,
    };
  },

  getters: {
    isKeySelected(state): (row: number, col: number) => boolean {
      return (row, col) =>
        state.selectedKey?.row == row && state.selectedKey?.col == col;
    },
  },

  actions: {
    reset() {
      this.configDetailsExpand = false;
      this.selectedArea = null;
      this.deselectKey();
      this.clearLedRules();
      this.showMousePlot = false;
    },

    selectKey(
      row: number,
      col: number,
      opts?: {
        expand?: boolean;
        action?: ActionInternal;
        transAction?: ActionInternal;
      }
    ) {
      this.selectedKey = { row, col };
      this.keyMenuExpand = opts?.expand ?? false;
      this.overrideKeyAction = opts?.action ?? null;
      this.overrideTransAction = opts?.transAction ?? null;
    },

    deselectKey() {
      this.selectedKey = null;
      this.keyMenuExpand = false;
      this.overrideKeyAction = null;
      this.overrideTransAction = null;
    },

    useLedRules(opts: { rules: LedRule[]; select?: number; expand?: boolean }) {
      this.selectedLedRule = opts.select ?? 0;
      this.expandLedRule = !!opts.expand;
      this.overrideLedRules = opts.rules;
    },

    clearLedRules() {
      this.selectedLedRule = null;
      this.expandLedRule = false;
      this.overrideLedRules = null;
    },
  },
});
